<template>
  <div>
    <CustomDialog
      v-model="dialog"
      ref="customDialog"
      backgroundColor="transparent"
      hideHeader
      hideFooter
      fullscreen
    >
      <div
        v-if="dialog"
        class="d-flex flex-column fill-height fill-width align-center justify-center dialog-content"
      >
        <v-img :src="require('@/assets/home/sunburst.png')" height="100vh">
          <div
            class="d-flex flex-column fill-height fill-width align-center justify-center pa-2"
          >
            <v-btn
              :disabled="loading.box || started"
              icon
              absolute
              top
              right
              @click.stop.prevent="tryCloseDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>

            <loader v-if="loading.content" />

            <template v-else>
              <div
                class="d-flex flex-wrap text-center text-h6 text-md-h5 font-weight-black font-italic bebas-neue"
              >
                TOQUE PARA ABRIR
              </div>

              <div class="d-flex justify-center mb-3">
                <div
                  :class="{
                    'animate__animated animate__headShake': shouldShake,
                    'animate__animated animate__tada': shouldPulse,
                  }"
                >
                  <MysteryBox
                    v-if="boxes[currentBox]"
                    ref="mysteryBox"
                    :box="boxes[currentBox]"
                    :image="draw.image"
                    :imageAux="draw.imageAux"
                    :loading="loading.box"
                    @started="handleStartedBox()"
                    @revealed="revealPrize()"
                  />
                </div>
              </div>

              <div
                v-if="boxes.length > 1"
                class="text-center text-h6 font-weight-black white--text mb-3"
              >
                {{ boxes.length }} Restantes
              </div>
            </template>
          </div>
        </v-img>

        <v-overlay :value="showPrize" z-index="999" opacity="0.3">
          <div
            v-if="!!boxes[this.currentBox]?.result"
            class="d-flex flex-column justify-center align-center mb-6"
          >
            <v-img
              :src="
                boxes[this.currentBox].result.image ||
                (boxes[this.currentBox].result.type === 'event'
                  ? require(`@/assets/prizes/${
                      boxes[this.currentBox].result.event
                    }.png`)
                  : require(`@/assets/prizes/${
                      boxes[this.currentBox].result.type
                    }.png`))
              "
              class="pump mb-2"
              max-width="8rem"
              contain
            />

            <div class="glass-container text-center px-6 py-2 rounded-xl">
              <div class="text-body-1 font-weight-black white--text">
                {{ boxes[this.currentBox].result.name }}
              </div>

              <div class="font-weight-bold text-caption">
                Raridade:

                <span :style="{ color: boxes[this.currentBox].result.color }">
                  {{ handleRarityLabel(boxes[this.currentBox].result.rarity) }}
                </span>
              </div>
            </div>
          </div>

          <div class="d-flex justify-center align-center">
            <v-btn class="btn" color="primary" rounded @click="handleNext()">
              <span>
                {{ boxes.length > 1 ? "Continuar" : "Fechar" }}
              </span>

              <v-icon right small>
                {{ boxes.length > 1 ? "fas fa-chevron-right" : "mdi-close" }}
              </v-icon>
            </v-btn>
          </div>
        </v-overlay>

        <Confetti v-if="confetti" />

        <loader-overlay v-model="loading.box" />
      </div>
    </CustomDialog>

    <BoxResult
      v-if="dialog"
      ref="boxResult"
      :prizes="_prizes"
      @close="close()"
    />
  </div>
</template>

<script>
import { useItem } from "@/services/items";
import { getItemDraw } from "@/services/draws";
import { handleRarityLabel } from "@/helpers";
import CustomDialog from "@/components/customDialog/CustomDialog";
import MysteryBox from "@/components/mysteryBox/MysteryBox";
import Confetti from "@/components/confetti/Confetti.vue";
import BoxResult from "./partials/boxResult/BoxResult.vue";
import "animate.css";

export default {
  name: "BoxDialog",

  components: {
    CustomDialog,
    MysteryBox,
    Confetti,
    BoxResult,
  },

  data() {
    return {
      loading: {
        content: true,
        box: false,
      },
      dialog: false,
      draw: null,
      currentBox: 0,
      boxes: [],
      backupBoxes: [],
      showPrize: false,
      confetti: false,
      shouldShake: false,
      shouldPulse: false,
      prizeSound: null,
      lostSound: null,
      started: false,
    };
  },

  beforeMount() {
    this.prizeSound = new Audio(require("@/assets/sounds/tada.mp3"));
    this.lostSound = new Audio(require("@/assets/sounds/negative.mp3"));
  },

  mounted() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
  },

  beforeDestroy() {
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },

  watch: {
    shouldShake(val) {
      if (val) {
        setTimeout(() => (this.shouldShake = false), 500);
      }
    },

    shouldPulse(val) {
      if (val) {
        setTimeout(() => (this.shouldPulse = false), 1000);
      }
    },
  },

  computed: {
    _prizes() {
      return this.backupBoxes.map((el) => el.result);
    },
  },

  methods: {
    async open(data) {
      this.dialog = true;

      const qty = Number(data?.qty) || 0;

      this.boxes = Array.from({ length: qty }, () => ({
        ...data.item,
        result: null,
      }));

      await this.getItemDraw();

      this.$nextTick(() => {
        this.resetBox();
      });
    },

    async useItem() {
      try {
        this.loading.box = true;

        const boxID = this.boxes[this.currentBox]._id;

        const payload = {
          target: "draws",
          targetID: boxID,
          qty: 1,
        };

        const { data } = await useItem(boxID, payload);

        this.boxes[this.currentBox].result = data;

        this.backupBoxes.push({ ...this.boxes[this.currentBox] });

        this.$refs.mysteryBox.handleReveal();

        this.$root.$emit("load-session");
      } catch (error) {
        this.$alert(error?.message, "error");
      } finally {
        this.loading.box = false;
      }
    },

    async getItemDraw() {
      try {
        const { data } = await getItemDraw(this.boxes[this.currentBox]._id);

        this.draw = data;
      } catch (error) {
        this.$alert(error?.message, "error");
      } finally {
        this.loading.content = false;
      }
    },

    handleStartedBox() {
      if (this.boxes[this.currentBox].result) return;
      this.started = true;

      this.useItem();
    },

    resetBox() {
      this.$refs.mysteryBox.resetBox();
    },

    revealPrize() {
      this.shouldPulse = true;

      const winner = this.boxes[this.currentBox].result.event !== "nothing";

      if (winner) this.triggerPrizeSound();
      else this.triggerLostSound();

      if (this.boxes[this.currentBox].result) {
        setTimeout(() => {
          this.started = false;
          this.showPrize = true;

          if (winner) this.confetti = true;
        }, 500);
      }
    },

    handleNext() {
      this.resetBox();

      if (this.boxes.length) this.boxes.splice(this.currentBox, 1);

      this.showPrize = false;
      this.confetti = false;
      this.shouldShake = true;

      if (!this.boxes.length) {
        this.$root.$emit("load-session");
        this.$refs.boxResult.open();
        return;
      }

      this.currentBox = 0;
    },

    triggerPrizeSound() {
      if (!this.prizeSound) return;

      const playPromise = this.prizeSound.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // ignore
        });
      }
    },

    triggerLostSound() {
      if (!this.lostSound) return;

      const playPromise = this.lostSound.play();

      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // ignore
        });
      }
    },

    handleBeforeUnload(event) {
      if (this.started) {
        event.preventDefault();
        event.returnValue =
          "Você já iniciou a abertura, aguarde antes de sair!";
        return event.returnValue;
      }
    },

    tryCloseDialog() {
      if (this.started) {
        this.$alert(
          "Você já iniciou a abertura, aguarde antes de sair!",
          "warning"
        );
        return;
      }

      this.close();
    },

    close() {
      this.loading.content = true;
      this.loading.box = false;
      this.dialog = false;
      this.draw = null;
      this.currentBox = 0;
      this.boxes = [];
      this.backupBoxes = [];
      this.showPrize = false;
      this.started = false;
    },

    handleRarityLabel,
  },
};
</script>

<style src="./style.scss" lang="scss" scoped />
